<template>
  <v-container>

    <v-toolbar dense>
      <v-toolbar-side-icon>
          <v-icon  @click="resetTimeTravel" color="primary">skip_previous</v-icon>
      </v-toolbar-side-icon>
      <v-toolbar-title>Dispatcher Space-Time Continuum</v-toolbar-title>
    </v-toolbar>

    <v-toolbar flat class="mt-2">
      <v-layout row>
        <v-text-field flat readonly persistent-hint
                      hint="Starting Time" prepend-inner-icon="access_alarms"
                      v-model="startingTimeDisplay">
          <template slot="append-outer">
            <v-img src="/static/flux-capacitor.png" height="50" width="50"/>
          </template>
        </v-text-field>
        <v-text-field flat readonly persistent-hint
                      hint="Future Time" append-icon="watch"
                      v-model="currentFutureTimeDisplay" class="pl-2 centered-input">
        </v-text-field>
      </v-layout>
    </v-toolbar>

    <v-card flat>
      <div class="pb-3">
        <GmapMap
          ref="mapRef"
          :center="mapCenter"
          :zoom="mapZoomLevel"
          map-type-id="roadmap"
          :style="styles">

          <gmap-info-window :options="infoOptions"
                            :position="infoWindowPos"
                            :opened="infoWinOpen"
                            @closeclick="closeClick()">
            <div v-html="infoContent"></div>
          </gmap-info-window>

          <GmapMarker v-if="bookedLoadPositionMarkers"
                      v-for="(positionMarker, idx) in bookedLoadPositionMarkers"
                      :key="'booked_load_markerId_'+idx"
                      :position="{lat: positionMarker.lat, lng: positionMarker.lng}"
                      :icon="bookedLoadMarkerPinIcon(positionMarker.load,
                     positionMarker.spaceTimeOrder)"
                      @click="$emit('selectBookedLoad', positionMarker.load)"
                      :label="{
                        text: positionMarker.load.mapId,
                        color: '#FFFFFF'
                      }"
                      @mouseover="toggleInfoWindow({
                          lat: positionMarker.lat,
                          lng: positionMarker.lng
                        }, positionMarker.infoText)"
                      :clickable="true"
                      :draggable="false"/>

          <GmapMarker v-if="plannedLoadPositionMarkers"
                      v-for="(positionMarker, idx) in plannedLoadPositionMarkers"
                      :key="'planned_load_markerId_'+idx"
                      :position="{lat: positionMarker.lat, lng: positionMarker.lng}"
                      :icon="plannedLoadMarkerPinIcon(positionMarker.load,
                      positionMarker.spaceTimeOrder)"
                      @click="$emit('selectPlannedLoad', positionMarker.load)"
                      @mouseover="toggleInfoWindow({
                          lat: positionMarker.lat,
                          lng: positionMarker.lng
                          }, positionMarker.infoText)"
                      :clickable="true"
                      :draggable="false"/>
      </GmapMap>
      </div>

      <v-layout row class="pt-2">
        <v-flex sm10 pr-2>
          <v-slider v-model="sliderValue"
                    min="0"
                    :max="spaceTimeSliderMaxValue"
                    ticks="always"
                    tick-size="1"
                    step="1"
                    thumb-label="always"
                    :color="currentColor"
                    :trackColor="timeMachineTrackColor"
                    :thumbColor="currentColor"
                    always-dirty
                    :hint="currentFutureDay"
                    persistent-hint
                    inverted-label
          >
            <template slot="prepend">
              <v-list-tile-action>
                <v-img src="/static/time-machine.png" height="50"/>
              </v-list-tile-action>
            </template>
            <template slot="thumb-label" slot-scope="props">
              <v-icon color="white">
                watch
              </v-icon>
            </template>
          </v-slider>
        </v-flex>
        <v-flex sm2>
          <v-select outline
                    height="12"
                    dense
                    :full-width="false"
                    label="Days into Future"
                    v-model="daysIntoFuture"
                    :items="daysIntoFutureOptions">
          </v-select>
        </v-flex>
      </v-layout>

    </v-card>

  </v-container>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';

import spaceTimeContinuumMixin from '@/mixins/spaceTimeContinuumMixin';

import * as Timestamps from '@/types/timestamp';
import * as Loads from '@/types/models/load';

export default {
  name: 'VSpaceTimeContinuum',
  mixins: [
    spaceTimeContinuumMixin,
  ],
  props: {
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '400px',
    },
    stcBookedLoads: {
      type: Array,
      default: () => [],
    },
    stcPlannedLoads: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    currentColor() {
      this.$emit('colorUpdate', this.currentColor);
    },
  },
  computed: {
    _() {
      return _;
    },
    daysIntoFutureOptions() {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    },
    currentColor() {
      const ratio = this.sliderValue / this.spaceTimeSliderMaxValue;
      const t = Math.floor(ratio * _.size(this.spaceTimeDistanceGradient));
      return this.spaceTimeDistanceGradient[t];
    },
    timeMachineTrackColor() {
      return _.last(this.spaceTimeDistanceGradient);
    },
    startingTimeDisplay() {
      return Timestamps.formattedSpaceTimeDate(this.startingTime);
    },
    currentFutureTime() {
      const plusHours = this.sliderValue * this.spaceTimeHoursPerTick;
      const plusDuration = moment.duration(plusHours, 'hours');
      return moment(this.startingTime).add(plusDuration);
    },
    currentFutureDay() {
      return Timestamps.formattedSpaceTimeDay(this.currentFutureTime);
    },
    currentFutureTimeDisplay() {
      return Timestamps.formattedSpaceTimeDate(this.currentFutureTime);
    },
    styles() {
      return `width: ${this.width}; height: ${this.height}`;
    },
    bookedLoadPositionMarkers() {
      const normalized = _.chain(this.stcBookedLoads)
        .filter(l => this.isBookedLoadVisibleFromTimeMachine(l));

      return _.chain(normalized)
        .map((l, i) => this.formatBookedLoadPickupPositionMarker(l, i))
        .reverse() // reverse so that the first pins are placed last (on top)
        .value();
    },
    plannedLoadPositionMarkers() {
      // plannedLoadMarkerPinIcon;
      const normalized = this.stcPlannedLoads.filter(l => this.isPlannedLoadVisibleFromTimeMachine(l));

      return _.chain(normalized)
        .map((l, i) => this.formatPlannedLoadPickupPositionMarker(l, i))
        .reverse() // reverse so that the first pins are placed last (on top)
        .value();
    },
  },
  methods: {
    resetTimeTravel() {
      this.startingTime = moment();
    },
    closeClick() {
      this.infoWinOpen = false;
    },
    toggleInfoWindow(marker, text) {
      this.infoWindowPos = marker;
      this.infoContent = text;
      this.infoWinOpen = true;
    },
    formatMarkerTooltip(load, description) {
      const formattedTime = this.$options.filters.formatDate(load.pickupTime);

      let htmlContent = `<div style="text-align: center"><span class="font-weight-bold">${load.humanId} - ${description}</span><br />`;
      if (load.nickname) {
        htmlContent += `<span class="font-weight-light mt-5">[${load.nickname} at  ${formattedTime}]</span>`;
      } else {
        htmlContent += `<span class="font-weight-light">[${formattedTime}]</span>`;
      }

      if (load.loadGroup) {
        const label = load.loadGroup.availableLoadCount > 1 ? 'loads' : 'load';
        htmlContent += `<br /><span class="font-weight-light">${load.loadGroup.availableLoadCount} ${label} available</span></div>`;

        if (load.bookedLoads.length) {
          htmlContent += '<span><b>Assignments</b></span></br>';
          htmlContent += '<ul>';
          load.bookedLoads.forEach((bookedLoad) => {
            htmlContent += `<li>${bookedLoad.humanId} - ${Loads.assignedTruckingCompanyDriverName(bookedLoad)}</li>`;
          });
          htmlContent += '</ul>';
        }
      }

      return htmlContent;
    },
    formatBookedLoadPickupPositionMarker(load, idx) {
      const pickupLocation = Loads.pickupLocationGooglePlace(load) || { what: 'the crap' };
      const description = this.bookedLoadShortDescription(load);
      const htmlContent = this.formatMarkerTooltip(load, description);

      return {
        load,
        lat: pickupLocation.lat,
        lng: pickupLocation.lng,
        infoText: htmlContent,
        loadId: load.humanId,
        spaceTimeOrder: (idx + 1),
      };
    },
    formatPlannedLoadPickupPositionMarker(load, idx) {
      const pickupLocation = this.loadGroupShippingLocation(load).googlePlace;
      const description = this.bookedLoadShortDescription(load);
      const htmlContent = this.formatMarkerTooltip(load, description);

      return {
        load,
        lat: pickupLocation.lat,
        lng: pickupLocation.lng,
        infoText: htmlContent,
        loadId: load.humanId,
        spaceTimeOrder: (idx + 1),
      };
    },

    bookedLoadMarkerPinIcon(load) {
      const color = this.spaceTimeBookedLoadGradient(load);

      return {
        color,
        path: 'M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '',
        strokeWeight: 0,
        scale: 0.65,
        labelOrigin: new window.google.maps.Point(0, -28),
      };
    },
    plannedLoadMarkerPinIcon(load) {
      const color = this.spaceTimePlannedLoadGradient(load);
      return {
        color,
        path: 'M22-48h-44v43h16l6 5 6-5h16z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '',
        strokeWeight: 0,
        scale: 0.5,
        labelOrigin: new window.google.maps.Point(0, -26),
      };
    },
  },
  data: () => ({
    startingTime: moment(),
    sliderValue: 0,
    locations: [],
    locationGroup: null,
    mapCenter: { lat: 37.090240, lng: -95.712891 },
    mapZoomLevel: 4,
    infoWinOpen: false,
    infoContent: false,
    infoWindowPos: { lat: 37.090240, lng: -95.712891 },
    infoOptions: {
      pixelOffset: {
        width: 0,
        height: -35,
      },
    },
  }),
  mounted() {
    this.$emit('colorUpdate', this.currentColor);
  },
};

</script>



// WEBPACK FOOTER //
// src/components/InfoWidgets/SpaceTimeContinuum.vue